import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../api";
import {
  ACCOUNT_REWARD_OPTIONS,
  ACCOUNT_REWARDS_EXPERIENCE,
} from "../../services/AdminApis/accountRewardsConstants";


// const localhost = "http://localhost:4000";

// TASK 48: the session cookie is now HOST-ONLY, so it is only sent to the
// host that issued it. Calling the API through the absolute apex origin
// (`https://promotly.cloud/api`) would therefore arrive WITHOUT a session
// cookie and 401. The tenant-portal API is same-origin (`api` uses baseURL
// '/api'), so these calls must use the relative path as well.

const initialState = {
  token: null,
  isAuthenticated: false,
  // START FALSE — session check is driven by App.js isCheckingSession (useState).
  // Starting true caused HMR loops: Redux resets → isLoadingAuth:true →
  // showLoading:true → early return blocks Routes → no re-check possible.
  // App.js checkSession() sets isLoadingAuth via fetchUserPermissions extraReducers.
  isLoadingAuth: false,
  user: null,
  permissions: null,
  account: null,
};

// Redemption Settings Endpoint - START

export const toggleRedemptionStatus = createAsyncThunk(
  "promotly/redemption-status",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.patch(
        "/account/redemption-settings/toggle-status"
      );
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

export const simplePointsConversion = createAsyncThunk(
  "promotly/simple-points",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.patch("/account/redemption-settings", {
        cross_redemption_enabled: data.crossRedemptionEnable,
        expiry_time_in_days: data.expiryTime,
        grace_period_in_days: data.gracePeriod || 0,
        points: data.conversionRatePoints,
        point_currency_unit: data.conversionRateEGP,
        min_threshold: data.minThreshold || 0,
      });
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Redemption Settings Endpoint - END

// Products Upload Endpoint - ADMIN - START

export const downloadProductTemplate = createAsyncThunk(
  "sassLoyalty/account/download-product-template",
  async (data, { rejectWithValue }) => {
    try {
      const language = data?.language || 'en';
      const result = await api.get(
        `/account/products/download-product-template?language=${language}`,
        {
          responseType: "blob",
        }
      );
      return result;
    } catch (error) {
      rejectWithValue(error.message);
    }
  }
);

export const uploadSingleProduct = createAsyncThunk(
  "sassLoyalty/account/upload-single-product",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.post(
        "/staging/v1/ecommerce/products/single-product",
        {
          sku: data.productSku,
          points: data.points,
          brand_id: data.brandID,
          price: data.productPrice,
          name: data.productName,
        }
      );
      return result;
    } catch (error) {
      rejectWithValue(error.message);
    }
  }
);

// Products Upload Endpoint - ADMIN - END

// Earning Rules - START

export const toggleEarningStatus = createAsyncThunk(
  `promotly/global-status`,
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.patch(
        `/account/global-settings/toggle-status`
      );
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

export const switchGlobalSettingsValueFixed = createAsyncThunk(
  "promotly/switch-global-on/value/fixed",
  async (data, { rejectWithValue }) => {
    try {
      // Use POST (not PATCH) as backend only defines POST route for creating/updating settings
      const result = await api.post(
        `/account/global-settings`,
        {
          earning_rule: "Value_Based_Fixed",
          point_value: Number(data.value),
          value_per_point: Number(data.valuePerPoint) || 0.01,
          minimum_purchase: data.minimumPurchase || 0,
          max_points_per_transaction: data.maxPoints || 0,
          points_expiration_days: 0,
          grace_period_days: 0,
          has_time_constraint: false
        }
      );
      return result;
    } catch (error) {
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

export const switchGlobalSettingsValueRatio = createAsyncThunk(
  "promotly/switch-global-on/value/ratio",
  async (data, { rejectWithValue }) => {
    try {
      // Use POST (not PATCH) as backend only defines POST route for creating/updating settings
      const result = await api.post(
        `/account/global-settings`,
        {
          earning_rule: "Value_Based_Ratio",
          point_value: Number(data.value),
          value_per_point: Number(data.valuePerPoint) || 0.01,
          minimum_purchase: data.minimumPurchase || 0,
          max_points_per_transaction: data.maxPoints || 0,
          points_expiration_days: 0,
          grace_period_days: 0,
          has_time_constraint: false
        }
      );
      return result;
    } catch (error) {
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

export const switchGlobalSettingsProducts = createAsyncThunk(
  "promotly/switch-global-on/products",
  async (data, { rejectWithValue }) => {
    try {
      // Use POST (not PATCH) as backend only defines POST route
      // Backend expects flat structure: scope_type, scope_ids, points_per_product
      const scopeType = data.scopeType || 'global';
      const scopeIds = data.scopeIds || [];
      const payload = {
        earning_rule: "Product_Based_Fixed",
        point_value: Number(data.pointsValue) || 0,
        value_per_point: Number(data.valuePerPoint) || 0.01,
        minimum_purchase: 0,
        max_points_per_transaction: 0,
        points_expiration_days: 0,
        grace_period_days: 0,
        has_time_constraint: false
      };
      if (scopeType && scopeType !== 'global' && scopeIds.length > 0) {
        payload.scope_type = scopeType;
        payload.scope_id = scopeIds.map(Number);
      }
      const result = await api.post(`/account/global-settings`, payload);
      return result;
    } catch (error) {
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

export const updateSellerEarningTypeOff = createAsyncThunk(
  "promotly/update-seller-earningType",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.patch(
        `/account/update-seller-settings`,
        {
          sellers: [{
            seller_id: data.sellerID,
            new_value: data.earningType
          }]
        }
      );
      return result;
    } catch (error) {
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Earning Rules - END

export const getSellers = createAsyncThunk(
  "promotly/get-sellers",
  async (_, { rejectWithValue }) => {
    try {
      const result = await api.get("/account/sellers-list");
      return result.data;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Tiers Settings - START
export const createCustomerTier = createAsyncThunk(
  "sassLoyalty/account/create-customer-tier",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.post("/staging/v1/ecommerce/tiers/customer/", {
        name: data.tierName,
        description: data.tierDesc,
        rule_type: "all",
        min_purchase_amount: data.minReceiptValue,
        min_transactions_count: data.minTransNum,
        is_active: true,
      });
      return result;
    } catch (error) {
      rejectWithValue(error.message);
    }
  }
);

export const createSellerTier = createAsyncThunk(
  "sassLoyalty/account/create-seller-tier",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.post("/staging/v1/ecommerce/tiers/seller/", {
        name: data.tierName,
        description: data.tierDesc,
        ruleType: "all",
        minimum_sales_volume: data.minSalesVolume,
        minimum_products: data.minProductsNumber,
        is_active: true,
      });
      return result;
    } catch (error) {
      rejectWithValue(error.message);
    }
  }
);
// Tiers Settings - END

// Rewards Options - START


function filterEmptyFields(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) =>
        value !== "" &&
        value !== null &&
        value !== undefined &&
        !(Array.isArray(value) && value.length === 0)
    )
  );
}

function buildRewardOptionPayload(fields) {
  const { description, ...rest } = fields;
  const payload = filterEmptyFields(rest);
  const desc =
    description != null && String(description).trim() !== ""
      ? String(description).trim()
      : payload.name;
  payload.description = desc || "Reward";
  return payload;
}

function mapDiscountToRewardOption(discountValues) {
  const isPercent = discountValues.type === "PERCENT";
  const reward_type = isPercent ? "DISCOUNT_PERCENT" : "DISCOUNT_FIXED";
  const points_required = discountValues.points_required;
  const discount = isPercent
    ? discountValues.maximum_discount ?? 0
    : discountValues.fixed_value ?? 0;
  return buildRewardOptionPayload({
    reward_type,
    name: discountValues.name,
    description: discountValues.description,
    percentage_value: isPercent ? discountValues.percentage_value : undefined,
    cross_seller_redemption: discountValues.cross_seller_redemption,
    denominations: [{ points_required, discount }],
  });
}

function mapGiftCardToRewardOption(giftCardValues) {
  const denominations = (giftCardValues.denominations || []).map((d) => ({
    points_required: d.points_required,
    discount: d.discount_amount ?? d.discount,
  }));
  return buildRewardOptionPayload({
    reward_type: "GIFT_CARD",
    name: giftCardValues.name,
    description: giftCardValues.description,
    categories: giftCardValues.categories,
    denominations,
  });
}

function mapFreeProductToRewardOption(freeProductValues) {
  const categories =
    freeProductValues.categories ||
    (Array.isArray(freeProductValues.brandCategoryCombinations)
      ? [
          ...new Set(
            freeProductValues.brandCategoryCombinations
              .map((c) => c.categoryId)
              .filter(Boolean)
          ),
        ]
      : undefined);

  return buildRewardOptionPayload({
    reward_type: "FREE_PRODUCT",
    name: freeProductValues.name,
    description: freeProductValues.description,
    redemption_limit: freeProductValues.redemption_limit,
    maximum_product_value: freeProductValues.maximum_product_value,
    free_product_id: freeProductValues.free_product_id,
    categories,
    denominations: [
      {
        points_required: freeProductValues.points,
        discount: freeProductValues.maximum_product_value,
      },
    ],
  });
}

export const createDiscountReward = createAsyncThunk(
  "promotly/rewards-options/discount",
  async (data, { rejectWithValue }) => {
    const cleanDiscountValues = filterEmptyFields(data.discountValues);
    try {
      const payload = mapDiscountToRewardOption(cleanDiscountValues);
      const result = await api.post(ACCOUNT_REWARD_OPTIONS, payload);
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

export const createFreeProductReward = createAsyncThunk(
  "promotly/rewards-options/freeProduct",
  async (data, { rejectWithValue }) => {
    try {
      const payload = mapFreeProductToRewardOption(data.freeProductValues || {});
      const result = await api.post(ACCOUNT_REWARD_OPTIONS, payload);
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

export const createGiftCardReward = createAsyncThunk(
  "promotly/reward-options/giftCard",
  async (data, { rejectWithValue }) => {
    const cleanGiftCardValues = filterEmptyFields(data.giftCardValues);
    try {
      const payload = mapGiftCardToRewardOption(cleanGiftCardValues);
      const result = await api.post(ACCOUNT_REWARD_OPTIONS, payload);
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Experience - create location
export const createExperienceLocation = createAsyncThunk(
  "promotly/experience/location/add",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.post(`${ACCOUNT_REWARDS_EXPERIENCE}/location`, {
        name: data.locationName,
        address: data.locationAddress,
        city_id: data.cityID,
      });
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Experience - update location
export const updateExperienceLocation = createAsyncThunk(
  "promotly/experience/location/update",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.put(`/rewards/experience/location/${data.id}`, {
        name: data.locationName,
        address: data.locationAddress,
        city_id: data.cityID,
      });
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Experience - delete location
export const deleteExperienceLocation = createAsyncThunk(
  "promotly/experience/location/delete",
  async (id, { rejectWithValue }) => {
    try {
      const result = await api.delete(`/rewards/experience/location/${id}`);
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Experience - create category
export const createExperienceCategory = createAsyncThunk(
  "promotly/experience/category/add",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.post("/rewards/experience/category", {
        name: data.catName,
        description:
          "Lorem Ipsum is simply dummy text of the printing and typesetting industry.",
        is_active: true,
      });
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Experience - update category
export const updateExperienceCategory = createAsyncThunk(
  "promotly/experience/category/update",
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.put(`/rewards/experience/category/${data.id}`, {
        name: data.name,
        description: data.description,
        is_active: data.is_active,
      });
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Experience - delete category
export const deleteExperienceCategory = createAsyncThunk(
  "promotly/experience/category/delete",
  async (id, { rejectWithValue }) => {
    try {
      const result = await api.delete(`/rewards/experience/category/${id}`);
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

export const createExperienceReward = createAsyncThunk(
  "promotly/reward-options/experience",
  async (data, { rejectWithValue }) => {
    const payload = buildRewardOptionPayload(data.experienceValues || {});
    try {
      const result = await api.post(ACCOUNT_REWARD_OPTIONS, payload);
      return result;
    } catch (error) {
      // The global axios interceptor in api.js will handle 401 errors and force logout
      return rejectWithValue(error.message || "Unknown error");
    }
  }
);

// Rewards Options - END

// Add Seller - START

export const addNewSeller = createAsyncThunk(
  "promotly/seller-management/add-seller",
  async (data, { rejectWithValue }) => {
    try {
      // Create request payload with seller data
      const sellerData = {
        owner_name: `${data.firstName} ${data.lastName}`,
        firstName: data.firstName,
        lastName: data.lastName,
        business_name: data.businessName,
        business_email: data.businessEmail,
        email: data.email,
        commercialID: data.commercialId,
        phone: data.phone, // Use the already formatted phone from frontend
        seller_type: data.sellerType,
        // CD-15 fix: the wizard collects subdomain + earning type on Step 4 but this
        // field-by-field payload rebuild silently dropped them, so multivendor sellers
        // were created with subdomain=NULL (seller-portal login 404: "Subdomain not found").
        subdomain: data.subdomain,
        earning_type: data.earning_type,
        has_portal: data.hasPortal,
        logo_image_url: data.logo_image_url, // Use the pre-uploaded image URL
        addressData: data.addressData, // Pass the entire addressData object
        seller_permissions: data.seller_permissions // Include seller permissions
      };

      // Send the request as JSON instead of FormData - use the configured api instance
      const result = await api.post(`/account/register-seller`, sellerData);

      // Check if the backend returned an error in the response body
      if (result.data && result.data.status === false) {
        return rejectWithValue(result.data.error || result.data.message || "Backend error");
      }
      return result.data; // Return result.data for fulfilled case
    } catch (error) {
      console.error('Error adding seller in thunk (HTTP error):', error.response?.data || error.message);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Unknown error";
      return rejectWithValue(errorMessage);
    }
  }
);

// Add Seller - END

// Brand Management - Add New Brand - START

export const addNewBrand = createAsyncThunk("promotly/brand-management/add-brand", async (data, { rejectWithValue }) => {
  try {
    const result = await api.post("/brands", {
      "name": data.brandName,
      "description": data.brandDesc,
      "category_id": data.catID,
      "seller_ids": data.sellerIDs
    })
    return result;
  } catch (error) {
    // The global axios interceptor in api.js will handle 401 errors and force logout
    return rejectWithValue(error.message || "Unknown error");
  }
})

export const addNewCategory = createAsyncThunk("promotly/brand-management/add-category", async (data, { rejectWithValue }) => {
  try {
    const result = await api.post("/categories", {
      "name": data.catName,
      "description": data.catDesc
    })
    return result;
  } catch (error) {
    // The global axios interceptor in api.js will handle 401 errors and force logout
    return rejectWithValue(error.message || "Unknown error");
  }
})

export const downloadBrandTemplate = createAsyncThunk("promotly/brand-management/download-brand-template", async (data, { rejectWithValue }) => {
  try {
    const result = await api.get("/brands/download-brand-template", {
      responseType: "blob",
    })

    const url = window.URL.createObjectURL(new Blob([result.data]));
    const link = document.createElement("a");
    link.href = url;

    link.setAttribute("download", "Brands-Upload-Template.xlsx");

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

    return result;
  } catch (error) {
    // The global axios interceptor in api.js will handle 401 errors and force logout
    return rejectWithValue(error.message || "Unknown error");

  }
})

export const uploadBulkBrands = createAsyncThunk("promotly/brand-management/upload-bulk-brands", async (data, { rejectWithValue }) => {
  console.log(data)
  const uploadBulkFormData = new FormData();
  if (data.uploadFile) {
    uploadBulkFormData.append("bands", data.uploadFile);
  }
  try {
    const result = await api.post(`/brands/upload-bulk-brands`, uploadBulkFormData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: data.onUploadProgress,
    })
    return result;
  } catch (error) {
    // The global axios interceptor in api.js will handle 401 errors and force logout
    return rejectWithValue(error.message || "Unknown error");
  }
})

// Brand Management - Add New Brand - END

// --- Feature Permissions Management ---
// Async thunk to fetch permissions from the backend
export const fetchUserPermissions = createAsyncThunk(
  "admin/fetchUserPermissions",
  async (_, { rejectWithValue }) => {
    console.log('[adminSlice.fetchUserPermissions] Starting to fetch user permissions');
    try {
      const response = await api.get('/auth/status');
      console.log('[adminSlice.fetchUserPermissions] Full response object:', response);
      console.log('[adminSlice.fetchUserPermissions] Response.data:', response.data);
      console.log('[adminSlice.fetchUserPermissions] Response.data.data:', response.data?.data);
      if (response.data?.status === "success") {
        console.log('[adminSlice.fetchUserPermissions] Successfully fetched permissions:', {
          user: response.data.data.user,
          account: response.data.data.account,
          permissions: response.data.data.permissions,
        });
        return {
          user: response.data.data.user,
          account: response.data.data.account,
          permissions: response.data.data.permissions
        };
      } else {
        console.log('[adminSlice.fetchUserPermissions] Failed to fetch permissions:', response.data);
        throw new Error('Failed to fetch user permissions');
      }
    } catch (error) {
      console.error('[adminSlice.fetchUserPermissions] Error fetching permissions:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setLoadingAuth: (state, action) => {
      state.isLoadingAuth = action.payload;
    },
    setAuthenticated: (state, action) => {
      // Accept action payload
      state.isAuthenticated = true;
      state.isLoadingAuth = false;
      state.user = action.payload; // Store the user data from the payload
      // Optionally clear token if session is now the primary auth method
      // state.token = null;
      // localStorage.removeItem('token');
    },
    // New action to set permissions
    setPermissions: (state, action) => {
      state.permissions = action.payload;
    },
    // New action to set account
    setAccount: (state, action) => {
      state.account = action.payload;
    },
    setToken: (state, action) => {
      // This is now mainly for the legacy login flow
      state.token = action.payload;
      state.isAuthenticated = !!action.payload; // Set authenticated if token is present
      state.isLoadingAuth = false; // Assume loading is done if token is set
      localStorage.setItem("token", action.payload); // Persist token
    },
    clearAuth: (state) => {
      state.token = "";
      state.isAuthenticated = false;
      state.isLoadingAuth = false; // Ensure loading is false after clearing
      state.permissions = null; // Clear permissions on logout
      state.account = null; // Clear account on logout
      localStorage.removeItem("token");
      localStorage.removeItem("userData");
      localStorage.removeItem("keycloakRealmId"); // Remove realmId on logout
    },
    handleLogout: (state, action) => {
      // Reset to initial state, ensuring loading is false
      return { ...initialState, isLoadingAuth: false };
    },
  },
  extraReducers: (builder) => {
    // Handle fetchUserPermissions
    builder.addCase(fetchUserPermissions.fulfilled, (state, action) => {
      console.log('[adminSlice.extraReducers.fulfilled] Received payload:', JSON.stringify(action.payload, null, 2));
      state.user = action.payload.user;
      state.account = action.payload.account;
      state.permissions = action.payload.permissions;
      console.log('[adminSlice.extraReducers.fulfilled] State.permissions set to:', JSON.stringify(state.permissions, null, 2));
      state.isAuthenticated = true;
      state.isLoadingAuth = false;
    });
    builder.addCase(fetchUserPermissions.pending, (state) => {
      state.isLoadingAuth = true;
    });
    builder.addCase(fetchUserPermissions.rejected, (state, action) => {
      state.isLoadingAuth = false;
      state.isAuthenticated = false;
      state.permissions = null;
      state.account = null;
    });

    // Handle addNewSeller
    builder.addCase(addNewSeller.fulfilled, (state, action) => {
      // No state changes needed here, success is handled in component
    });
    builder.addCase(addNewSeller.pending, (state) => {
      // Optional: set a loading state for seller creation if needed
    });
    builder.addCase(addNewSeller.rejected, (state, action) => {
      // The error message is already returned by rejectWithValue in the thunk
      // No state changes needed here, error is handled in component
    });
  },
});

// Export the new actions along with existing ones
export const {
  setToken,
  clearAuth,
  handleLogout,
  setAuthenticated,
  setLoadingAuth,
  setPermissions,
  setAccount,
} = adminSlice.actions;
export default adminSlice.reducer;
